"use client";

import { IconButton, Box, Backdrop, Portal } from "@mui/material";
import { FC, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { DeleteConfirm } from "./deleteConfirm";
import { EditConfirm } from "./editConfirm";
import ShareIcon from '@mui/icons-material/Share';
import ShareButton from "@/shared/ShareClient";
import { useQuery } from "@tanstack/react-query";
import { clipSharesForCsvShareOptions } from "@/queryOptions/clipShares";

enum ClipsShareActionsEnum {
  NONE = "none",
  EDIT = "edit",
  DELETE = "delete",
  SHARE = "share",
}

export const ClipsShareActionsMenu: FC<{
  clipShareId: number;
  bookmarkId: number;
  title: string;
  onOpenChange?: (isOpen: boolean) => void;
}> = ({ clipShareId, bookmarkId, title, onOpenChange }) => {
  const [actionOpen, setActionOpen] = useState<ClipsShareActionsEnum>(ClipsShareActionsEnum.NONE);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };
  const { data: clipShareData } = useQuery(clipSharesForCsvShareOptions(clipShareId));

    const handleOpenAction = (action: ClipsShareActionsEnum) => {
    setActionOpen(action);
    setAnchorEl(null); // Close menu when opening an action
  };

  const handleClose = () => {
    setAnchorEl(null);
    onOpenChange?.(false);
    setActionOpen(ClipsShareActionsEnum.NONE);
  };

  return (
    <>
      <div className="flex-shrink-0">
        <IconButton
          aria-label="more options"
          aria-controls={open ? 'demo-positioned-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={(e) => {
            handleClick(e);
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="clips-share-actions-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              className: "mr-8"
            }
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={() => handleOpenAction(ClipsShareActionsEnum.EDIT)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenAction(ClipsShareActionsEnum.DELETE)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText className="text-error">Delete</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenAction(ClipsShareActionsEnum.SHARE)}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        </Menu>
      </div>

      <Portal>
        <Backdrop
          open={actionOpen === ClipsShareActionsEnum.EDIT}
          className="z-[1300] flex items-center justify-center"
          onClick={handleClose}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-lg"
          >
            <EditConfirm
              clipShareId={clipShareId}
              bookmarkId={bookmarkId}
              currentTitle={title}
              handleClose={handleClose}
            />
          </Box>
        </Backdrop>
      </Portal>

      <Portal>
        <Backdrop
          open={actionOpen === ClipsShareActionsEnum.DELETE}
          className="z-[1300] flex items-center justify-center"
          onClick={handleClose}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-lg"
          >
            <DeleteConfirm
              clipShareId={clipShareId}
              bookmarkId={bookmarkId}
              handleClose={handleClose}
            />
          </Box>
        </Backdrop>
      </Portal>

      {actionOpen === ClipsShareActionsEnum.SHARE && clipShareData?.meeting_id && (
        <ShareButton
          clipShareData={clipShareData}
          transcriptText={clipShareData.raw_text}
          handleClose={handleClose}
        />
      )}
    </>
  );
};

